import React, { useState, useEffect } from 'react';
import { IFmsApp, IAppScreen, ScreenTypes, ScreenContentUtils } from './MobileAppTypes';
import { toast } from 'react-toastify';
import Button from '../_Core/Button';
import Modal from '../Modal/Modal';
import MobileAppScreenEditor from './MobileAppScreenEditor';
import Icon, { IconType } from '../Icon/Icon';
import { UiValuePairs } from '../../utils/CommonTypes/UserTypes';
import { FormatDate } from '../../utils/Tools';

interface IProps {
  app: IFmsApp;
  show: boolean;
  onClose: () => void;
  onSave: (app: IFmsApp, screen: IAppScreen, fileDic: Record<string, File>) => Promise<boolean>;
  onRemove: (app: IFmsApp, screen: IAppScreen) => void;
  existingClientFiles: UiValuePairs[];
}

const MobileAppScreenManager: React.FC<IProps> = (props: IProps) => {
  const [screens, setScreens] = useState<IAppScreen[]>([]);
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [editingScreen, setEditingScreen] = useState<IAppScreen | undefined>(undefined);

  useEffect(() => {
    if (props.app.Screens) {
      // Sort screens by OrderInMenu
      const sortedScreens = [...props.app.Screens].sort((a, b) => a.OrderInMenu - b.OrderInMenu);
      setScreens(sortedScreens);
    }
  }, [props.app]);

  useEffect(() => {
    console.log('Editing screen changed:', editingScreen);
  }, [editingScreen]);

  const handleAddScreen = () => {
    setEditingScreen(undefined);
    setShowEditor(true);
  };

  const handleEditScreen = (screen: IAppScreen) => {
    setEditingScreen(screen);
    setShowEditor(true);
  };

  const handleDuplicateScreen = (screen: IAppScreen) => {
    const duplicatedScreen: IAppScreen = {
      ...screen,
      Title: `${screen.Title} (Copy)`,
      MenuTitle: `${screen.MenuTitle} (Copy)`,
      OrderInMenu: screens.length,
    };
    setEditingScreen(duplicatedScreen);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingScreen(undefined);
  };

  const handleSaveScreen = (screen: IAppScreen, fileDic: Record<string, File>) => {
    props.onSave(props.app, screen, fileDic).then((success) => {
      if (success) {
        handleCloseEditor();
      }
    });
  };

  const handleRemoveScreen = (screen: IAppScreen) => {
    props.onRemove(props.app, screen);
  };

  const getScreenTypeIcon = (type: string): IconType => {
    switch (type) {
      case ScreenTypes.FILE_PAGE:
        return IconType.invoice;
      case ScreenTypes.FAQ_PAGE:
        return IconType.questionCircle;
      case ScreenTypes.FORMATTED_TEXT_PAGE:
        return IconType.edit;
      case ScreenTypes.PARENT_SCREEN:
        return IconType.invoice;
      case ScreenTypes.NOTIFICATION_SCREEN:
        return IconType.bell;
      case ScreenTypes.WEBVIEW_SCREEN:
        return IconType.internet;
      case ScreenTypes.WEBLINK_SCREEN:
        return IconType.link;
      case ScreenTypes.SIMPLE_TEXT_PAGE:
      default:
        return IconType.invoice;
    }
  };

  const getScreenTypeLabel = (type: string): string => {
    switch (type) {
      case ScreenTypes.FILE_PAGE:
        return 'File Page';
      case ScreenTypes.FAQ_PAGE:
        return 'FAQ Page';
      case ScreenTypes.FORMATTED_TEXT_PAGE:
        return 'Formatted Text';
      case ScreenTypes.PARENT_SCREEN:
        return 'Parent Screen';
      case ScreenTypes.SIMPLE_TEXT_PAGE:
        return 'Simple Text';
      case ScreenTypes.NOTIFICATION_SCREEN:
        return 'Notification';
      case ScreenTypes.WEBVIEW_SCREEN:
        return 'Web View';
      case ScreenTypes.WEBLINK_SCREEN:
        return 'Web Link';
      default:
        return 'Unknown';
    }
  };

  const getContentPreview = (screen: IAppScreen): string => {
    const content = screen.JsonContentDic; //ScreenContentUtils.deserialize(screen.JsonContent);
    if (!content) return 'No content';

    switch (screen.Type) {
      case ScreenTypes.FILE_PAGE:
        const fileContent = content as any;
        return fileContent.fileName || 'Untitled file';

      case ScreenTypes.FAQ_PAGE:
        const faqContent = content as any;
        return `${faqContent.faqLength || 0} FAQs`;

      case ScreenTypes.PARENT_SCREEN:
        const parentContent = content as any;
        const childCount = parentContent.childScreenLength || 0;
        return `${childCount} child screens`;

      case ScreenTypes.SIMPLE_TEXT_PAGE:
      case ScreenTypes.FORMATTED_TEXT_PAGE:
        const textContent = content as any;
        const text = textContent.content || '';
        return text.length > 50 ? `${text.substring(0, 50)}...` : text;

      default:
        return 'No preview available';
    }
  };

  if (!props.show) return null;

  return (
    <Modal setModalOpen={props.onClose} title={`Manage Screens - ${props.app.Name}`} size="xl">
      <div className="mobile-app-screen-manager">
        {/* Header */}
        <div className="form-section">
          <div className="flex-between">
            <div>
              <h4>App Screens ({screens.length})</h4>
              <p className="text-muted">Manage your app screens. Changes will be saved when you click "Save App".</p>
            </div>
            <div>
              <Button type="new" text="Add Screen" onClick={handleAddScreen} />
            </div>
          </div>
        </div>

        {/* Screens List */}
        <div className="form-section">
          {screens.length === 0 ? (
            <div className="text-center" style={{ padding: '40px', color: '#666' }}>
              <Icon type={IconType.phone} />
              <h5>No screens configured</h5>
              <p>Click "Add Screen" to create your first mobile app screen.</p>
            </div>
          ) : (
            <div className="screens-list">
              {screens.map((screen, index) => (
                <div
                  key={`${screen.OrderInMenu}-${screen.Title}`}
                  className={`screen-item ${!screen.ShowInMenu ? 'hidden-in-menu' : ''}`}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '15px',
                    margin: '10px 0',
                    backgroundColor: '#fff',
                  }}>
                  <div className="flex-between">
                    <div className="flex" style={{ alignItems: 'center', gap: '15px' }}>
                      {/* Screen Icon and Type */}
                      <div style={{ textAlign: 'center', minWidth: '60px' }}>
                        <Icon type={getScreenTypeIcon(screen.Type)} />
                        <div style={{ fontSize: '0.8em', color: '#666' }}>{getScreenTypeLabel(screen.Type)}</div>
                      </div>

                      {/* Screen Details */}
                      <div style={{ flex: 1 }}>
                        <div className="flex" style={{ alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                          <strong>{screen.Title}</strong>
                          {!screen.ShowInMenu && !screen.ShowInLowMenu && (
                            <span className="badge" style={{ backgroundColor: '#999', color: 'white', fontSize: '0.7em' }}>
                              Hidden
                            </span>
                          )}
                          {screen.IsSecure && (
                            <span className="badge" style={{ backgroundColor: '#f0ad4e', color: 'white', fontSize: '0.7em' }}>
                              Secure
                            </span>
                          )}
                          <span className="badge" style={{ backgroundColor: '#5bc0de', color: 'white', fontSize: '0.7em' }}>
                            {screen.AccessRole}
                          </span>
                          {(screen.DisplayStartAtUtc || screen.DisplayEndAtUtc) && (
                            <span className="badge" style={{ backgroundColor: '#8e44ad', color: 'white', fontSize: '0.7em' }}>
                              Scheduled: {screen.DisplayStartAtUtc ? `From ${FormatDate(screen.DisplayStartAtUtc, 'pretty')}` : 'No start limit'}
                              {' — '}
                              {screen.DisplayEndAtUtc ? `Until ${FormatDate(screen.DisplayEndAtUtc, 'pretty')}` : 'No end limit'}
                            </span>
                          )}
                        </div>

                        <div style={{ color: '#666', fontSize: '0.9em' }}>Menu: {screen.MenuTitle}</div>

                        <div style={{ color: '#999', fontSize: '0.8em', marginTop: '5px' }}>{getContentPreview(screen)}</div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex" style={{ gap: '5px' }}>
                      <Button type="edit" text="" iconOnly extraSmall onClick={() => handleEditScreen(screen)} />
                      <Button type="copy" text="" iconOnly extraSmall onClick={() => handleDuplicateScreen(screen)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="form-section">
          <div className="flex-between">
            <div>
              <Button type="new" text="Add Screen" onClick={handleAddScreen} />
            </div>
            <div>
              <Button type="close" text="Cancel" onClick={props.onClose} />
            </div>
          </div>
        </div>

        {/* Screen Editor */}
        {showEditor && (
          <MobileAppScreenEditor
            screen={editingScreen}
            show={showEditor}
            onSave={handleSaveScreen}
            onClose={handleCloseEditor}
            onRemove={handleRemoveScreen}
            existingClientFiles={props.existingClientFiles}
            availableScreens={screens}
          />
        )}
      </div>
    </Modal>
  );
};

export default MobileAppScreenManager;
